<template id="add-factor-or-group-dialog">
	<modal-dialog
		:title="actionLabel"
		executeText="Add to Group"
		:executeAction="addFactorOrGroup"
		class="add-factor-or-group-modal"
		:isDisabled="!thresholdFormValid"
	>
		<v-btn
			:class="[
				{
					secondary:
						expanded.find((row) => row.label === item.label) !==
						undefined,
				},
			]"
			slot="activator"
			:disabled="!thresholdFormValid"
			>{{ actionLabel }}</v-btn
		>

		<div slot="content">
			<form>
				<v-radio-group v-model="radioGroup">
					<v-data-table
						class="add-factor-or-group-modal-list"
						:headers="headers"
						:items="availableItems"
						:mobile-breakpoint="0"
						:items-per-page="availableItems.length"
						hide-default-footer
					>
						<template v-slot:item.radio="{ item }">
							<v-radio :label="item.label"></v-radio>
						</template>
						<template v-slot:item.treeMode="{ item }">
							<div v-if="item.treeMode === 'Parent'">Group</div>
							<div v-else>Factor</div>
						</template>
					</v-data-table>

					<div class="display-selected-info">
						<template
							v-if="availableItems[radioGroup] !== undefined"
						>
							<h3 class="display-selected-info-label">
								{{ availableItems[radioGroup].label }}
							</h3>
							<div class="display-selected-into-type">
								<span v-if="availableItems[radioGroup].treeMode === 'Parent'">Group</span>
								<span v-else>Factor</span>
							</div>
							<h4 class="display-selected-info-section-header">
								Description
							</h4>
							<p>{{ availableItems[radioGroup].description }}</p>
							<h4 class="display-selected-info-section-header">
								Why It Works
							</h4>
							<p>{{ availableItems[radioGroup].whyItWorks }}</p>
						</template>
					</div>
				</v-radio-group>
				<template v-if="addableGroupsAndFactors.length <= 0">
					<v-alert
						class="info"
						border="left"
						colored-border
						icon="mdi-information-outline"
					>
						All factors and factor groups are already in use for
						this profile.
					</v-alert>
				</template>
			</form>
		</div>
	</modal-dialog>
</template>

<script>
	/**
	 * @fileoverview add-factor-or-group-dialog.vue allows the user to add a group or factor to their profile's factor configuration.
	 * It relies on vuex to map the state variables from the store files.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from "vuex";

	export default {
		props: {
			/**
			 * Parent of the added group or factor
			 */
			item: {
				required: true,
			},
			/**
			 * List of expanded items
			 */
			expanded: {
				type: Array,
			},
			/**
			 * Boolean flag for allowing to add groups and factors
			 */
			groups: {
				type: Boolean,
				default: false,
			},
			/**
			 * Fraud type: True or Friendly
			 */
			type: {
				type: String,
				required: true
			}
		},
		data() {
			return {
				/**
				 * Headers for the list of available groups and factors
				 */
				headers: [
					{
						text: "Name",
						align: "start",
						sortable: false,
						value: "radio",
					},
					{
						text: "Scope",
						value: "scope",
						class: "add-factor-scope-header",
						cellClass: "add-factor-scope-cell"
					},
					{
						text: "Type",
						value: "treeMode",
						class: "add-factor-type-header",
						cellClass: "add-factor-type-cell"
					},
				],
				radioGroup: 0,	// selected radio button
			};
		},
		computed: {
			...mapState({
				addableFriendlyFraudGroupsAndFactors: (state) =>
					state.config.addableFriendlyFraudGroupsAndFactors,
				addableTrueFraudGroupsAndFactors: (state) =>
					state.config.addableTrueFraudGroupsAndFactors,
				parentChildrenMap: (state) => state.config.parentChildrenMap,
				factorConfigurations: (state) => state.config.factorConfigurations,
				friendlyFraudFactorsOnly: (state) => state.config.addableFriendlyFraudFactorsOnly,
				trueFraudFactorsOnly: (state) => state.config.addableTrueFraudFactorsOnly,
				thresholdFormValid: (state) => state.config.thresholdFormValid,
				loggedInUser: (state) => state.account.loggedInUser
			}),
			/**
			 * Return the available items based on if groups are allowed
			 * @returns either addable groups and factors or the factors only array
			 */
			availableItems() {
				if (this.groups === true) {
					return this.addableGroupsAndFactors;
				}

				return this.factorsOnly;
			},
			/**
			 * Return the correct label based on if groups are allowed
			 * @returns string label for the add button
			 */
			actionLabel() {
				return this.groups == true ? "Add Factor or Group" : "Add a Factor";
			},
			/**
			 * Return the correct groups and factors array
			 * @returns either friendly fraud or true fraud addable groups and factors array
			 */
			addableGroupsAndFactors() {
				if (this.type === "Friendly Fraud") {
					return this.addableFriendlyFraudGroupsAndFactors;
				} else {
					return this.addableTrueFraudGroupsAndFactors;
				}
			},
			/**
			 * Return the correct factors only array
			 * @returns either the true fraud or friendly fraud factors only array
			 */
			factorsOnly() {
				if (this.type === "Friendly Fraud") {
					return this.friendlyFraudFactorsOnly;
				} else {
					return this.trueFraudFactorsOnly;
				}
			}
		},
		methods: {
			/**
			 * Add factor or group to the profile's configuration:
			 * 		Add the factor or group to the parentChildrenMap
			 * 		Set the thresholds to the default thresholds
			 *  	Add the factor or group to the factor configurations
			 * 		Remap the factor configurations
			 * 		Remove factor or group from the list
			 * 		Emit an audit log message to the parent component
			 */
			addFactorOrGroup() {
				if (this.thresholdFormValid) {
					var addedFactor = undefined;
					if (this.groups) {
						addedFactor = this.addableGroupsAndFactors[this.radioGroup];
					} else {
						addedFactor = this.factorsOnly[this.radioGroup];
					}

					// add factor or group to parent
					addedFactor.parentId = this.item.factorId;

					if (!this.parentChildrenMap[this.item.factorId]) {
						this.parentChildrenMap[this.item.factorId] = [];
					}
					if (addedFactor.factorTreeList !== undefined) {
						addedFactor.factorTreeList = [];
					}
					
					this.parentChildrenMap[this.item.factorId].push(addedFactor);

					// Set thresholdValue = default values for each threshold in the addedFactor
					addedFactor.thresholds.map((threshold) => {
						threshold.levels.inputFields.map((inputField) => {
							inputField.fieldValue = inputField.fieldDefault;
						});
					});

					// Add factor to factorConfigurations
					var factorObj = {
						factorConfiguration: addedFactor,
					};
					this.factorConfigurations.push(factorObj);

					// Remap factorConfigurations
					this.$store.dispatch("createFactorConfigMap");

					// remove factor or group from list
					var index = undefined;

					index = this.factorsOnly.findIndex(
						(factor) => factor.factorId === addedFactor.factorId
					);

					if (index !== undefined) {
						this.factorsOnly.splice(index, 1);
					}

					index = this.addableGroupsAndFactors.findIndex(
						(factor) => factor.factorId === addedFactor.factorId
					);

					if (index !== undefined) {
						this.addableGroupsAndFactors.splice(index, 1);
						this.$store.dispatch("createFactorsOnlyList");
					}

					// set profileChanged
					this.$store.dispatch("setProfileChanged", true);

					// set configurationValid
					if (addedFactor.treeMode !== "Parent" ) {
						this.$store.dispatch("setConfigurationValid", true);
					} else {
						this.$store.dispatch("setConfigurationValid", false);
						const notification = {
							status: "warning",
							message: "All groups require at least one factor."
						};
						this.$store.dispatch("notifications/addNotification", notification);

						//emit to parent to force navigation into added factor
						this.$emit("changeParent", addedFactor);
					}

					// emit adding message to auditMessages
					let message = this.loggedInUser.name + ' added a ' + (addedFactor.treeMode === "Parent" ? 'category group: ' : 'factor: ') + addedFactor.label;
					this.$emit("addAuditMessage", message);
				} else {
					const notification = {
						status: "error",
						message:
							"Fix missing or invalid settings before adding to the group.",
					};
					this.$store.dispatch(
						"notifications/addNotification",
						notification
					);
				}
			},
		},
	};
</script>